import React, { memo } from "react";
import * as PropTypes from "prop-types";
import { databasePropType } from "../../../propTypes";
import { withStyles } from "@material-ui/styles";
import AsynchronousDataWidget from "../../widgets/AsynchronousDataWidget";
import CountryLevelInfluenceBox from "./CountryLevelInfluenceBox";
import CumulativeGapByGniGroupChartWidget from "./CumulativeGapByGniGroupChartWidget";
import CumulativeGapBySectorChartWidget from "./CumulativeGapBySectorChartWidget";
import CurrentSpendingBySectorTableWidget from "./CurrentSpendingBySectorTableWidget";
import CurrentSpendingChartWidget from "./CurrentSpendingChartWidget";
import HyperLink from "../../widgets/HyperLink";
import InfographicBox from "../../box/InfographicBox";
import ProjectedFundingGapsByGniGroupTableWidget from "./ProjectedFundingGapsByGniGroupTableWidget";
import ProjectedFundingGapsBySectorTableWidget from "./ProjectedFundingGapsBySectorTableWidget";
import ProjectedFundingGapsWithinPublicSectorTableWidget from "./ProjectedFundingGapsWithinPublicSectorTableWidget";
import Reference from "../../widgets/Reference";
import SmallFigure from "../layout/SmallFigure";
import ThemeIntroductionController from "../layout/ThemeIntroductionController";
import TextOnly from "../layout/TextOnly";
import spendingInfographic from "../../../images/spending_infographic.svg";
import MultiFigure from "../layout/MultiFigure";

const styles = {
  spendingInfographic: {
    maxWidth: 960
  }
};

const Theme1 = props => {
  const database = props.database;
  const data = props.data;

  return (
    <section id="theme1" className="theme">
      <ThemeIntroductionController
        reference="01"
        title="Prospects of Stagnating Donor&nbsp;Funds"
      >
        The new data and analytic approaches used in the CGA 2019 report have
        allowed us to unpack our spending estimates to examine variations within
        and among each GNI country group, sector, and source of funding.
        Conversations within the reproductive health community suggest that
        donor support for commodity procurement is declining and will likely
        continue to decline or, at best, remain stagnant in the decade to come.
        In the context of shrinking donor funding, it is important to understand
        the role donors play in different countries and what can be done to
        minimize the impact of limited funding on those women who need it most.
        Nearly half of spending within low-income countries comes from
        donors&mdash;prioritizing donor funding amongst lower-income countries,
        therefore, will be critical, as will encouraging country governments to
        play a larger role in funding family planning commodities.
      </ThemeIntroductionController>

      {/*Figure 1.1*/}
      <InfographicBox
        reference="Figure 1.1"
        title="Current Spending on Contraceptive Supplies"
      >
        <img
          src={spendingInfographic}
          alt="spending infographic"
          className={props.classes.spendingInfographic}
        />
      </InfographicBox>

      <TextOnly>
        <p>
          The amount currently spent on contraceptive supplies across the entire
          LMIC market is $3.33 billion. Over 80% of this amount&mdash;four out
          of every five dollars&mdash;comes from individuals purchasing supplies
          from private sector entities. Within the private sector, most spending
          is on supplies sold at full commercial prices (98%). Only a small
          percentage of private sector spending on supplies&mdash;just 2% ($46.7
          million)&mdash;is spent on subsidized supplies. These supplies are
          typically but not exclusively sold through social marketing agencies.
          The balance of spending comes from public sector sources, which
          include both donors and country governments. Governments, however,
          spend nearly three times more than donors.
        </p>
      </TextOnly>

      <MultiFigure
        largeFigure={
          // Table 1.1
          <AsynchronousDataWidget
            loaded={!!(data && data.currentSpendingBySectorTable)}
          >
            <CurrentSpendingBySectorTableWidget
              data={data ? data.currentSpendingBySectorTable : []}
              onDownloadCsv={() => {
                props.onDownloadCsv("theme1.currentSpendingBySectorTable");
              }}
            />
          </AsynchronousDataWidget>
        }
        smallFigure={
          // Figure 1.2
          <AsynchronousDataWidget
            loaded={!!(data && data.currentSpendingChart)}
          >
            <CurrentSpendingChartWidget
              data={data ? data.currentSpendingChart : []}
            />
          </AsynchronousDataWidget>
        }
      >
        <h4 className="subsection-heading">
          The Impact of Donor Spending within GNI Groups
        </h4>
        <p>
          Within each GNI Group, there are pronounced differences in public
          versus private sector spending, and thus the impact of shifts in donor
          spending. The public sector accounts for the largest share of spending
          in low-income countries. This is related in large part to the
          contribution of donors, which accounts for nearly 50% of all spending
          in this GNI group. The remaining public sector spending&mdash;roughly
          17%&mdash;comes from governments. Amongst lower-middle-income
          countries, public sector spending accounts for a smaller share (35%)
          of total spending in large part as a result of the smaller role of
          donor support (6%). But the level of country government expenditures
          (29%) compared to that of low-income countries ensures that public
          sector spending remains significant overall. In upper-middle-income
          countries, the contribution of public sector resources is negligible:
          out-of-pocket purchases from private sector entities (92%) dominate
          all spending.
        </p>
        <p>
          Because the level of spending varies so much across GNI groups, it is
          useful to look at not only how things are distributed within GNI
          groups but also how spending in each sector is distributed across GNI
          groups.
        </p>
        <p>
          Currently, donor funding appears to be well targeted towards those who
          need it most: nearly 60% of spending by donors on contraceptive
          supplies is spent to procure commodities for low-income countries,
          with most of the rest spent in lower-middle-income countries.
        </p>
        <p>
          More than half of government spending (56%) comes from
          lower-middle-income country governments. While government spending
          ($170 million) only makes up a small share (8%) of total spending in
          upper-middle-income countries, it represents 37% of total government
          spending.
        </p>
        <p>
          Overall, two-thirds of total spending comes from upper-middle-income
          countries, most of which is private sector spending.
        </p>
      </MultiFigure>

      {/*Box 1.1*/}
      <SmallFigure figure={<CountryLevelInfluenceBox />}>
        <p>
          While these patterns can easily be interpreted to imply some sort of
          natural, inevitable transition from public to private sector spending
          as country GNI increases, they are by no means a prescription for what
          the future holds. These patterns are all moments in time and
          representative of those countries currently falling within each GNI
          group today.
        </p>
        <p>
          There are also suggestions that the relatively smaller contribution of
          public sector expenditures within upper-middle-income countries may be
          specific to spending on contraceptive supplies. A wider analysis of
          health commodity expenditures suggests a potential resurgence of
          government spending not seen in this analysis among
          upper-middle-income countries.
          <Reference marker={2}>
            <HyperLink href="https://www.cgdev.org/publication/initial-estimation-size-health-commodity-markets-low-and-middle-income-countries">
              An Initial Estimation of the Size of Health Commodity Markets in
              Low- and Middle-Income Countries.
            </HyperLink>
          </Reference>
          This may be due to unique policy decisions about the role of public
          sector spending on contraception among some of the large
          upper-middle-income countries.
        </p>
        <p>
          These findings suggest that shortfalls in donor funding that may
          appear minor in the overall context could have a disastrous impact
          should they occur in low-income countries. At the same time, these
          findings also suggest that because of their larger revenue bases,
          governments in middle-income countries&mdash;both upper and
          lower&mdash;may be better positioned to provide funding for supplies
          and thus may be more capable of absorbing costs previously borne by
          donors.
        </p>
      </SmallFigure>

      <TextOnly sectionHeading="Funding Gaps">
        <p>
          If the amount spent annually on contraceptive supplies remains at the
          current level while the number of women who need supplies continues to
          grow, a funding gap of $178 million will emerge in 2020. As the
          discrepancy between total consumption costs and current spending on
          supplies continues to grow, so will the funding gap, reaching $266
          million in 2025 with the cumulative gap over five years
          (2021&ndash;2025) reaching $1.17 billion. A funding gap of this size
          could have devastating consequences for women who wish to prevent or
          delay pregnancy.
        </p>
      </TextOnly>

      {/*Table 1.2*/}
      <AsynchronousDataWidget
        loaded={!!(data && data.projectedFundingGapsBySectorTable)}
      >
        <ProjectedFundingGapsBySectorTableWidget
          database={database}
          data={data ? data.projectedFundingGapsBySectorTable : []}
          onChange={props.onWidgetChange}
          onDownloadCsv={settings => {
            props.onDownloadCsv(
              "theme1.projectedFundingGapsBySectorTable",
              settings
            );
          }}
        />
      </AsynchronousDataWidget>

      <TextOnly subSectionHeading="Funding Gaps by Sector">
        <p>
          Currently, women purchasing their own contraceptives from private
          entities make up the largest share of spending. Looking forward, this
          group will account for the largest share of the gap. The amount that
          will need to be spent out-of-pocket in the private sector (referred to
          as the private sector cost share
          <Reference marker={3} trailingSpace={false}>
            The cost share is calculated by GNI Group. The total consumption
            cost for each GNI Group multiplied by the share of current spending
            attributed to each sector (donor, government, private). The total
            cost share for each sector is summed from the cost share in each of
            the three GNI groups.
          </Reference>
          ) will increase to $2.85 billion in 2025, meaning women will need to
          spend $144 million more than they currently do
          <Reference marker={4} trailingSpace={false}>
            Gaps are calculated within each GNI group then summed to the total.
            Since there are different patterns of growth in each GNI group as
            well as different patterns of current spending, the overall shares
            of the gap across sector differ to the current share of spending.
          </Reference>
          . Because of differential patterns of growth across countries,
          however, a smaller share of the cumulative gap (59%) is attributed to
          private out-of-pocket expenditure than the cost share attributed to
          this group (80%).
        </p>
        <p>
          The remaining 41% of the cumulative gap is attributed to public sector
          entities ($481 million). This share of the gap is more than double the
          current cost share attributed to the public sector (20%). Because
          increases in consumption costs are concentrated in countries with a
          higher reliance on public sector funding, the gap will also be
          concentrated in these countries.
        </p>
      </TextOnly>

      {/*Table 1.3*/}
      <AsynchronousDataWidget
        loaded={!!(data && data.projectedFundingGapsWithinPublicSectorTable)}
      >
        <ProjectedFundingGapsWithinPublicSectorTableWidget
          database={database}
          data={data ? data.projectedFundingGapsWithinPublicSectorTable : []}
          onChange={props.onWidgetChange}
          onDownloadCsv={settings => {
            props.onDownloadCsv(
              "theme1.projectedFundingGapsWithinPublicSectorTable",
              settings
            );
          }}
        />
      </AsynchronousDataWidget>

      <TextOnly>
        <p>
          Despite the fact that governments account for a larger share of
          current spending than donors they end up over the 2021&ndash;2025
          period with a similar cumulative gap ($240 million versus $241
          million). This is due to the differences in growth patterns between
          low-income countries, where donors account for the majority of public
          sector funding, and lower-middle-income countries, where governments
          account for the majority of public sector funding. Donors will thus
          need to increase their funding more than governments&mdash;in both
          relative and absolute terms&mdash;in order to maintain their share.
        </p>
      </TextOnly>

      {/*Table 1.4*/}
      <AsynchronousDataWidget loaded={!!data}>
        <ProjectedFundingGapsByGniGroupTableWidget
          database={database}
          data={data ? data.projectedFundingGapsByGniGroupTable : []}
          onChange={props.onWidgetChange}
          onDownloadCsv={settings => {
            props.onDownloadCsv(
              "theme1.projectedFundingGapsByGniGroupTable",
              settings
            );
          }}
        />
      </AsynchronousDataWidget>

      <TextOnly subSectionHeading="Funding Gaps by GNI Group">
        <p>
          A closer look at the different patterns of change in consumption costs
          within each GNI group provides a varied view of future funding gaps.
          Because the total consumption cost is declining in upper middle-income
          countries, the gap in these countries will shrink over time; by 2025
          the spending gap will equal only 1% of the cost share. On the other
          hand, low-income countries will incur the largest increase in
          consumption costs, thus causing the gap in these countries to grow the
          fastest. In 2020, low-income countries will have the smallest gap,
          representing 18% of the total cost share that year. By 2025, their gap
          will be the second largest&mdash;nearly as large as that in
          lower-middle-income countries. By 2025, the gap will represent 37% of
          low-income countries’ total cost share. Therefore, in order to keep up
          with the growing needs in low-income countries, spending will need to
          grow much more rapidly relative to current spending levels.
        </p>
      </TextOnly>

      {/*Figure 1.3*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <CumulativeGapBySectorChartWidget
              data={data ? data.cumulativeGapBySectorChart : []}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          The total cumulative five-year gap across all low-and-middle-income
          countries will total $1.17 billion. The gap will be largest in lower
          middle-income countries ($521 million), followed by low-income
          countries ($419 million), and upper-middle-income countries ($233
          million).
        </p>
        <p>
          Private sector out-of-pocket spending by individuals accounts for the
          largest share of the cumulative gap within lower-middle-income
          countries (65%), as well as upper-middle-income countries (92%). In
          contrast, nearly half of the gap within low-income countries (49%) is
          attributed to donors alone, with the total gap in public sector
          funding reaching 66%.
        </p>
      </SmallFigure>

      {/*Figure 1.4*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <CumulativeGapByGniGroupChartWidget
              database={database}
              data={data ? data.cumulativeGapByGniGroupChart : []}
            />
          </AsynchronousDataWidget>
        }
        figureOnLeft={true}
      >
        <p>
          As with other results by GNI Group, it is also useful to look at how
          the gap attributed to each sector will be distributed across each the
          three groups. As seen earlier, both donors and governments will have a
          similar cumulative five-year gap. The donor gap, however, will be
          almost entirely concentrated within low-income countries, where it
          will account for nearly half (49%) of the funding gap in those
          countries. Meanwhile, just under two-thirds (63%) of the government
          funding gap will be concentrated in lower-middle-income countries.
        </p>
        <p>
          Despite the majority of private sector consumption costs coming from
          upper-middle-income countries, they will only account for 31% of the
          private sector gap. The gap will skew more towards the other two GNI
          groups, where the largest increases in cost will take place.
        </p>
        <p>
          These results show the important role that donors play, especially
          within low-income countries. Should donors fail to increase their
          spending, governments or women paying out-of-pocket in the private
          sector will have to make up the shortfall. Governments must already
          increase their spending, just to keep up with the growing costs.
        </p>
      </SmallFigure>
    </section>
  );
};

Theme1.propTypes = {
  database: databasePropType,
  data: PropTypes.object,
  onWidgetChange: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired
};

export default memo(withStyles(styles)(Theme1));
